import React, { useEffect, useRef, useState } from 'react';

import { InputProps } from '@material-ui/core';
import { Autocomplete } from '@react-google-maps/api';

import { LocalStorageService } from '../../services/localStorage.service';

import { StyleProps, useStyles } from './style';

interface Props {
  setPlace: (place: google.maps.places.PlaceResult) => void;
  label?: string;
  setFieldError?: (field: string, message: string | undefined) => void;
  initialValue?: string;
  externalError?: string;
  countryCode?: string;
}

export const BusinessPlaceAutocomplete: React.FC<InputProps & StyleProps & Props> = ({
  placeholder,
  inputFontSize,
  height,
  setPlace,
  label,
  setFieldError,
  initialValue,
  externalError,
  countryCode,
}) => {
  const classes = useStyles({ inputFontSize, height });
  const [instance, setInstance] = useState<google.maps.places.Autocomplete | null>(null);
  const [error, setError] = useState<string | null>(null);

  const ref = useRef<HTMLInputElement | null>(null);

  const onLoad = (autocomplete: google.maps.places.Autocomplete) => {
    setInstance(autocomplete);
  };

  const onPlaceChanged = () => {
    if (!instance) return;
    const companyName = instance.getPlace().name?.trim();
    if (ref.current && companyName) {
      ref.current.value = companyName;
    }
    setPlace(instance.getPlace());
    setError(null);
  };

  const onInputChange = (e: any) => {
    if (instance?.getPlace()?.name !== e.target.value) {
      setPlace({ ...(instance?.getPlace() || {}), name: e.target.value });
      setError(null);
    }
    if (!e.target?.value?.trim()) {
      setError('Required field');
    }
  };

  const onBlur = () => {
    if (!ref.current) {
      return;
    }
    if (instance && instance.getPlace() === undefined) {
      setPlace({ name: ref.current.value });
      setError(null);
    }
  };

  useEffect(() => {
    if (ref.current && initialValue) {
      ref.current.value = initialValue;
      setError(null);
    }
  }, [initialValue]);

  useEffect(() => {
    setError(externalError || null);
  }, [externalError]);

  return (
    <div className={classes.root}>
      <label htmlFor={label} className={classes.label}>
        {label}
      </label>
      {error && <div className={classes.toolTip}>{error}</div>}
      <Autocomplete
        onLoad={onLoad}
        onPlaceChanged={onPlaceChanged}
        className={classes.root}
        restrictions={{
          country: countryCode || LocalStorageService.getItem('country') || [],
        }}
        options={{
          types: ['establishment'],
        }}
      >
        <input
          className={classes.input}
          autoComplete='chrome-off'
          type={'search'}
          placeholder={placeholder}
          id={label}
          ref={ref}
          onBlur={onBlur}
          onChange={onInputChange}
        />
      </Autocomplete>
    </div>
  );
};
